import { TAB_IDS } from '../assets/data'
import { caseTypeOptions } from './systemData'
import { crimeVictimFunctions } from './crimeVitim'
import { isManagingAttorney } from './primaryItemScripts'
import { traverseObject } from './script'

const FOLDER_CONFIG = {
    case_negotiation: {
        ignoreFields: ['case_id', 'entity', 'entity_type', 'entity_type_id'],
    },
    crime_victim: {
        specificFields: ['filled_by_patient'],
    },
    worker_comp: {
        ignoreFields: [
            'wc_205',
            'wc_206',
            'created_at',
            'worker_comp_departments',
            'updated_at',
            'is_primary_insurance',
            'active',
            'id',
        ],
    },
}

/**
 * Work-queue drawer tab `id` → `tabs_button[].tab` used on the patient case page.
 * Drawer ids that already match `tab` need no entry.
 */
export const DRAWER_TAB_TO_PATIENT_TAB = {
    case_details_drawer: 'case_summary',
    flags: 'flags',
    history: 'history',
}

/**
 * Same rules as `PatientTabs` (case type whitelist, managing attorney, crime victim,
 * data presence for "remaining" tabs, ASC when no surgeries).
 *
 * @param {{ activeCase: object, permission_tabs: any[], foundItem: object }} args
 * @returns {any[]}
 */
export function computeVisiblePatientTabItems({ activeCase, permission_tabs, foundItem }) {
    if (!activeCase || !foundItem || !Array.isArray(permission_tabs)) {
        return []
    }

    const allowedByCaseType = new Set(foundItem.tabs)
    let difference = permission_tabs.filter((x) => allowedByCaseType.has(x.tab))

    if (!isManagingAttorney(activeCase)) {
        const removeProco = new Set([TAB_IDS.PROCO_DOCS])
        difference = difference.filter((x) => !removeProco.has(x.id))
    }

    if (!crimeVictimFunctions.isDUIorHitAndRun(activeCase)) {
        if (!traverseObject(activeCase.crime_victim, null, FOLDER_CONFIG.crime_victim.specificFields)) {
            const removeCv = new Set([TAB_IDS.CRIMEVICTIM])
            difference = difference.filter((x) => !removeCv.has(x.id))
        }
    }

    if (activeCase.surgeries && activeCase.surgeries.length == 0) {
        const removeAsc = new Set([TAB_IDS.ASC])
        difference = difference.filter((x) => !removeAsc.has(x.id))
    }

    return difference.sort((x, y) => new Date(x.order) - new Date(y.order))
}

/**
 * Resolve case-type row for the current case (same lookup as PatientTabs).
 */
export function getCaseTypeTabConfig(activeCase) {
    if (!activeCase?.case?.case_type_id) {
        return null
    }
    return caseTypeOptions()?.find((item) => item.value == activeCase.case.case_type_id) ?? null
}

export function drawerTabMatchesPatientVisibility(drawerTabId, visibleTabItems) {
    if (visibleTabItems == null) {
        return true
    }
    if (!Array.isArray(visibleTabItems) || visibleTabItems.length === 0) {
        return false
    }
    const patientTabKey = DRAWER_TAB_TO_PATIENT_TAB[drawerTabId] ?? drawerTabId
    return visibleTabItems.some((v) => v.tab === patientTabKey)
}

/**
 * Work-queue Overview (`CaseDetailTab`) blocks → `tabs_button[].tab` for the same visibility as patient tabs.
 * `practice` is grouped with case summary (no separate practices tab on the patient strip).
 */
export const CASE_DETAIL_SECTION_TAB = {
    case: 'case_summary',
    attorney: 'attorney',
    practice: 'case_summary',
    liability: 'liability',
    medpay_pip: 'medpay_pip',
    um: 'um',
    major_med: 'major_med',
    workers_comp: 'workers_comp',
    services: 'services',
}

export function isCaseDetailSectionVisible(sectionKey, visibleTabItems) {
    if (visibleTabItems == null) {
        return true
    }
    if (!Array.isArray(visibleTabItems) || visibleTabItems.length === 0) {
        return false
    }
    const tabKey = CASE_DETAIL_SECTION_TAB[sectionKey]
    if (tabKey == null) {
        return true
    }
    return visibleTabItems.some((v) => v.tab === tabKey)
}
